import { startGlobalLoading, stopGlobalLoading } from '@components/loaders/globalLoading';
import PageTitle from '@components/pageTitle';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getGLDocByAccountAndPeriod, getGLAccountsList } from '../api';
import { GLAccountModel, GlDocumentSql, PaginatedDataGLDoc, SelectOptionAndValue } from '@common/modelDefinition';
import translate from '@data/translations';
import dayjs from 'dayjs';
import InlineRangeInput from '@components/inlineRangeInput';
import useStore from '@data/state/zustand';
import Select from 'react-select';
import { customStyles } from '../utils';
import PaginationButtons from '@components/buttons/pagination';
import { openInNewTab } from '@pages/helpAndInformations/helpAndSupport';
import PrintView from '@components/printView';
import { CompanyHeaderDataForPrit } from '../glReport/printPage';
import { ReactComponent as Print } from '../../../assets/custom/icons/printer.svg';
import { formatNumber } from '@components/numberFormat';

interface AccountCardsProps {
    history: any;
}

function AccountCards(props: AccountCardsProps) {
    const [printView, setPrintView] = useState(false as boolean);
    const [allGLAccounts, setAllGLAccounts] = useState([] as GLAccountModel[]);
    const [showGLCards, setShowGLCards] = useState(null as SelectOptionAndValue | null);
    const [glDocsForAccount, setGLDocsForAccount] = useState({ data: [], dataCount: 0 } as PaginatedDataGLDoc);
    const [from, setFrom] = useState(new Date(dayjs().startOf('month').valueOf()));
    const [to, setTo] = useState(new Date());
    const [account, setAccount] = useState(null as string | null);
    const [dataCount, setDataCount] = React.useState(0 as number);
    const [showDPColumn, setShowDPColumn] = React.useState(true as boolean);
    const [showPDColumn, setShowPDColumn] = React.useState(true as boolean);
    const [page, setPage] = useState(0);
    const rows = 50;

    const otherSettings = useStore.getState().otherSettings;

    useEffect(() => {
        loadData().catch((err) => {
            console.log(err);
        });
        // eslint-disable-next-line
    }, []);

    const loadData = async () => {
        try {
            startGlobalLoading();
            const _glAccounts = await getGLAccountsList(null, false, null, true);
            setGLDocsForAccount({ data: [], dataCount: 0 });
            setAllGLAccounts(_glAccounts ? [..._glAccounts] : []);

            if (props.history?.location?.search?.split('=')?.[1]) {
                const glAcc = _glAccounts?.find(
                    (gl) => gl.account === props.history?.location?.search?.split('=')?.[1]
                );
                if (glAcc) {
                    setShowGLCards(glAcc ? { label: glAcc.name, value: glAcc.account } : null);
                    await getPageData(glAcc?.account, page);
                }
            }
            stopGlobalLoading();
        } catch (error: any) {
            stopGlobalLoading();
            toast(error.message, {
                position: toast.POSITION.TOP_RIGHT,
                type: toast.TYPE.ERROR,
            });
        }
    };

    const getPageData = async (glAcc: string, _page: number = page, _from: Date = from, _to: Date = to) => {
        const data: PaginatedDataGLDoc = await getGLDocByAccountAndPeriod(
            glAcc,
            _page,
            rows,
            _from?.getTime(),
            _to?.getTime()
        );
        setGLDocsForAccount({ ...data });
        setDataCount(data?.dataCount);
        setPage(_page);
    };

    React.useEffect(() => {
        window.addEventListener('beforeprint', () => {
            setPrintView(true);
        });

        return () => {
            window.removeEventListener('beforeprint', () => {
                return;
            });
        };
        // eslint-disable-next-line
    }, [printView]);

    React.useEffect(() => {
        window.addEventListener('afterprint', () => {
            setPrintView(false);
        });
        return () => {
            window.removeEventListener('afterprint', () => {
                return;
            });
        };
        // eslint-disable-next-line
    }, [printView]);

    React.useEffect(() => {
        if (printView) {
            window.print();
            setPrintView(false); // Resetovanje printView nakon printanja, ako želite da se vrati nazad
        }
    }, [printView]);

    const handleDateSelection = async (type: string, date: any) => {
        const _from = type === 'from' ? date : from;
        const _to = type === 'to' ? date : to;
        setFrom(_from);
        setTo(_to);
        setGLDocsForAccount({ data: [], dataCount: 0 });

        if (getDaysBetween(_from, _to) > 40) {
            toast(translate('Maximum date range is 40 days'), { type: toast.TYPE.ERROR });
        } else if (account) {
            await getPageData(account, page, _from, _to);
        }
    };

    const glAccValues = allGLAccounts?.map((acc: GLAccountModel) => {
        return {
            value: acc.account,
            label: acc.account + ' - ' + acc.name,
        };
    });

    if (printView) {
        return (
            <PrintView
                items={<div></div>}
                documentHeader={
                    <div className="col-12 mt-2">
                        <PrintHeader
                            from={new Date(from)}
                            to={new Date(to)}
                            account={account}
                            glDocsForAccount={glDocsForAccount}
                            otherSettings={otherSettings}
                            showDPColumn={showDPColumn}
                            showPDColumn={showPDColumn}
                        />
                    </div>
                }
                documentFooter={<div className="col-12 mb-3"></div>}
                footerHeight={52}
                headerHeight={52}
            />
        );
    }

    return (
        <div className="mobile-page">
            <div className="title-with-options display-flex space-between">
                <PageTitle />
                <div className="display-flex">
                    {glDocsForAccount && glDocsForAccount?.data?.length > 0 ? (
                        <div className="flex-center">
                            {' '}
                            <div
                                onClick={() => {
                                    setShowDPColumn(!showDPColumn);
                                }}
                                className="flex-center mr-8p "
                            >
                                <span className={'button-white-w-icon-only mr-4p pt-4p pb-4p pl-4p pr-4p'}>
                                    {translate('Show column Balance D-P')}
                                </span>
                                <div
                                    className={
                                        'pointer ' +
                                        (showDPColumn ? 'fa fa-check-square-o fa-lg' : 'fa fa-square-o fa-lg')
                                    }
                                ></div>
                            </div>
                            <div
                                onClick={() => {
                                    setShowPDColumn(!showPDColumn);
                                }}
                                className="flex-center mr-8p "
                            >
                                <span className={'button-white-w-icon-only mr-4p pt-4p pb-4p pl-4p pr-4p'}>
                                    {translate('Show column Balance P-D')}
                                </span>
                                <div
                                    className={
                                        'pointer ' +
                                        (showPDColumn ? 'fa fa-check-square-o fa-lg' : 'fa fa-square-o fa-lg')
                                    }
                                ></div>
                            </div>
                        </div>
                    ) : null}
                    <div
                        className="print-dark pointer mr-16p"
                        onClick={() => {
                            setPrintView(!printView);
                        }}
                    >
                        <Print />
                    </div>
                </div>
            </div>

            <div className="flex-center">
                <div className="search-and-page-controls display-flex space-between align-center mr-16p">
                    <InlineRangeInput initialFrom={from} initialTo={to} onChange={handleDateSelection} />
                </div>

                <Select
                    isMulti
                    placeholder={translate('Choose account')}
                    options={glAccValues}
                    name="type"
                    onChange={async (option: any) => {
                        if (option === null || option?.length === 0) {
                            setGLDocsForAccount({ data: [], dataCount: 0 });
                            setShowGLCards(null);
                            setAccount(null);
                        } else {
                            setShowGLCards(option[option.length - 1]);
                            setAccount(option[option.length - 1].value);
                            await getPageData(option[option.length - 1].value, page);
                        }
                    }}
                    value={showGLCards ? showGLCards : translate('Choose account')}
                    styles={customStyles}
                ></Select>

                <div className="ml-auto">
                    <PaginationButtons
                        rows={rows}
                        page={page}
                        setPage={async (_page: number) => {
                            if (showGLCards) {
                                await getPageData(showGLCards?.value, _page);
                            }
                        }}
                        dataCount={dataCount}
                    />
                </div>
            </div>

            <br />
            {glDocsForAccount && glDocsForAccount?.data?.length > 0 ? (
                <GLAccountCardList
                    otherSettings={otherSettings}
                    glDocsForAccount={glDocsForAccount}
                    showDPColumn={showDPColumn}
                    showPDColumn={showPDColumn}
                />
            ) : showGLCards ? (
                <div className="custom-list-item">{translate('No documents for selected period')}</div>
            ) : null}
        </div>
    );
}

export default AccountCards;

interface GLAccountCardListProps {
    glDocsForAccount: PaginatedDataGLDoc;
    otherSettings: any;
    showDPColumn: boolean;
    showPDColumn: boolean;
}

export const GLAccountCardList = (props: GLAccountCardListProps) => {
    const { glDocsForAccount, otherSettings, showPDColumn, showDPColumn } = props;

    let totalCredit = 0.0,
        totalDebt = 0.0;

    for (const glDoc of glDocsForAccount?.data) {
        totalCredit = totalCredit + Number(glDoc?.credit);
        totalDebt = totalDebt + Number(glDoc?.debt);
    }

    let currentDebtBalance = 0.0,
        currentCreditBalance = 0.0;

    return (
        <div className="p-2">
            <div className="custom-list-header">
                <div>{translate('Account')}</div>
                <div>{translate('Doc.num.')}</div>
                <div>{translate('Doc.name')}</div>
                <div>{translate('Issue date')}</div>
                <div>{translate('Posting date')}</div>
                <div>{translate('Type')}</div>
                <div>{translate('Class')}</div>
                <div>{translate('Debt')}</div>
                <div>{translate('Credit')}</div>
                {showDPColumn ? <div className="justify-right">{translate('Balance D-P')}</div> : null}
                {showPDColumn ? <div className="justify-right">{translate('Balance P-D')}</div> : null}
                <div></div>
            </div>

            {glDocsForAccount?.data?.map((gl: GlDocumentSql, index: number) => {
                const debtBalance = Number(gl.debt) - Number(gl.credit);
                currentDebtBalance = currentDebtBalance + Number(debtBalance);

                const creditBalance = Number(gl.credit) - Number(gl.debt);
                currentCreditBalance = currentCreditBalance + Number(creditBalance);

                return (
                    <div
                        className="custom-list-item"
                        key={index}
                        onClick={() => {
                            if (process.env.NODE_ENV === 'development') {
                                openInNewTab(`http://localhost:5555/#/glDocuments/edit/${gl?.glDocumentId}`);
                            } else {
                                openInNewTab(window.location.origin + `/#/glDocuments/edit/${gl?.glDocumentId}`);
                            }
                        }}
                    >
                        <div>{gl.account}</div>
                        <div>{gl.docNumber}</div>
                        <div>{translate(gl.name)}</div>
                        <div>
                            {gl.issueDate
                                ? dayjs(gl.issueDate).format(
                                      otherSettings?.dateFormat ? otherSettings.dateFormat : 'DD/MM/YYYY'
                                  )
                                : ''}
                        </div>
                        <div>
                            {gl.postingDate
                                ? dayjs(gl.postingDate).format(
                                      otherSettings?.dateFormat ? otherSettings.dateFormat : 'DD/MM/YYYY'
                                  )
                                : ''}
                        </div>
                        <div>{gl.type ? translate(gl.type) : ''}</div>
                        <div>{gl.class ? translate(gl.class) : ''}</div>
                        <div>{formatNumber(gl.debt)}</div>
                        <div>{formatNumber(gl.credit)}</div>
                        {showDPColumn ? (
                            <div className="text-bold justify-right">{formatNumber(currentDebtBalance)}</div>
                        ) : null}
                        {showPDColumn ? (
                            <div className="text-bold justify-right">{formatNumber(currentCreditBalance)}</div>
                        ) : null}
                        <div></div>
                    </div>
                );
            })}

            <div className="custom-list-item mt-10p">
                <div className="text-bold">{translate('TOTAL')}</div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div className="text-bold">{formatNumber(totalDebt)}</div>
                <div className="text-bold">{formatNumber(totalCredit)}</div>
                {showDPColumn ? (
                    <div className="text-bold justify-right ">{formatNumber(currentDebtBalance)}</div>
                ) : null}
                {showPDColumn ? (
                    <div className="text-bold justify-right">{formatNumber(currentCreditBalance)}</div>
                ) : null}
                <div></div>
            </div>
        </div>
    );
};

interface PrintHeaderProps {
    from: Date;
    to: Date;
    account: string | null;
    glDocsForAccount: PaginatedDataGLDoc;
    otherSettings: any;
    showDPColumn: boolean;
    showPDColumn: boolean;
}

const PrintHeader = (props: PrintHeaderProps) => {
    const { from, to, account, glDocsForAccount, otherSettings, showDPColumn, showPDColumn } = props;

    return (
        <div>
            <CompanyHeaderDataForPrit />
            <br />
            <div className="print-title">{translate('Account cards')}</div>
            <div className="print-date">
                ({translate('Account')}{" "} {account ? account : '-'}, {translate('from')}{' '}
                {dayjs(from).format(otherSettings?.dateFormat ? otherSettings.dateFormat : 'DD/MM/YYYY')}{' '}
                {translate('to')}{' '}
                {dayjs(to).format(otherSettings?.dateFormat ? otherSettings.dateFormat : 'DD/MM/YYYY')})
            </div>
            <GLAccountCardList
                glDocsForAccount={glDocsForAccount}
                otherSettings={otherSettings}
                showDPColumn={showDPColumn}
                showPDColumn={showPDColumn}
            />
        </div>
    );
};

export const getDaysBetween = (startDate: number, endDate: number): number => {
    const diffTime = Math.abs(endDate - startDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Pretvaramo milisekunde u dane
};
